import React from 'react'
import {observer} from 'mobx-react'
import {Modal} from '../../../../components/modal'
import {BaseButton, Box, Button, Flex, Stack, Text, useDisclosure} from '../../../../vanilla'
import {useHistory} from 'react-router-dom'
import {useBasketStore, useRapidDeliveryStore} from '../../../../store/hooks/useStore'
import {FormattedNumber} from 'react-intl'
import {Countdown} from '../../../../components/book-delivery/reservation-status'
import {RapidLabelIcon, RapidDeliverToHomeIcon} from '../../../../components/icons'
import {months} from '../../../../constants'
import {DateUtils} from '../../../../utils/date-utils'
import DeliveryRestrictionNotice, {
  getDeliveryRestrictionMessage,
} from '../../../../components/delivery-restriction-notice'
import { LocationState } from '../../../../types/forms'

export const RapidDeliveryConfirmationModal = observer(
  ({...rest}: ReturnType<typeof useDisclosure>) => {
    const history = useHistory()
    const locationState = history.location.state as LocationState | undefined
    const {shippingAddress, basket, addressDisplay, shippingMethod} = useBasketStore()
    const {rapidDeliveryShippingCost, rapidDeliveryDeliveryEstimate, rapidDeliveryReservation} =
      useRapidDeliveryStore()

    const handleContinueShopping = () => {
      if (locationState?.originPath) {
        history.push(locationState.originPath)
        return
      }

      if (history.length > 1) {
        history.goBack()
        return
      }

      history.push('/offers')
    }

    const today = new Date()
    const day = DateUtils.getOrdinal(today.getDate())
    const month = months[today.getMonth()]

    const expiryTime = rapidDeliveryReservation?.expiryTime
    const showRestrictionNotice = !!getDeliveryRestrictionMessage(shippingMethod, {
      isRapidOrder: true
    })

    return (
      <Modal {...rest} size="bookingConfirmation" closeIcon={false} allowScroll={true}>
        <Box data-test-selector="rapid-delivery-confirmation-modal">
          <Text variant="text6" textAlign="center" paddingX="28px" paddingY="16px">
            Rapid Delivery Booked
          </Text>

          {/* Date box + SVG */}
          <Flex
            paddingY="24px"
            paddingX="20px"
            style={{
              background: '#f3f3f3',
            }}
            gap="20px"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box style={{minWidth: '86px'}}>
              <Box backgroundColor="accent0Dark" paddingX="4px" paddingY="8px">
                <Text
                  variant="unstyled"
                  fontSize="xs"
                  fontWeight="bold"
                  textAlign="center"
                  color="white"
                  lineHeight="none"
                >
                  Today
                </Text>
              </Box>
              <Box
                border="2px"
                borderColor="accent0Dark"
                paddingX="4px"
                paddingY="10px"
                backgroundColor="white"
              >
                <Text textAlign="center" variant="text6">
                  {day}
                </Text>
                <Text textAlign="center" variant="text2" lineHeight="none">
                  {month}
                </Text>
              </Box>
            </Box>
            <RapidDeliverToHomeIcon paddingRight="12px" style={{width: '200px', height: '55px'}} />
          </Flex>

          {/* Delivery info */}
          <Flex padding="24px" gap="20px" direction="column">
            <Stack data-test-selector="rapid-delivery-slot-summary" width="full">
              <Flex alignItems="center" justifyContent="space-between" width="full">
                <Text color="accent1" variant="text3">
                  Your delivery:
                </Text>
                <RapidLabelIcon width="64px" height="24px" />
              </Flex>
              <Text variant="text5" data-test-selector="rapid-delivery-estimate-summary">
                Today, within {rapidDeliveryDeliveryEstimate ?? 60} minutes
              </Text>
              <Text variant="text3" data-test-selector="rapid-delivery-shipping-cost">
                Delivery:{' '}
                <FormattedNumber
                  style="currency"
                  currency={basket?.currency ?? 'GBP'}
                  value={rapidDeliveryShippingCost ?? 0}
                />
              </Text>
            </Stack>

            {showRestrictionNotice ? (
              <Stack alignSelf="stretch" data-test-selector="rapid-delivery-restriction-notice">
                <DeliveryRestrictionNotice shippingMethod={shippingMethod} isRapidOrder={true} />
              </Stack>
            ) : null}

            <Stack data-test-selector="rapid-delivery-address-summary">
              <Text color="accent1" variant="text3">
                We'll deliver to:
              </Text>
              <Text variant="text3" data-cs-mask="" data-test-selector="rapid-delivery-address-id">
                {shippingAddress?.c_customerAddressID}
              </Text>
              <Text variant="text3" data-cs-mask="" data-test-selector="rapid-delivery-address-display">
                {addressDisplay}
              </Text>
            </Stack>
          </Flex>

          {/* Countdown */}
          <Box paddingY="12px" paddingX="24px" marginBottom="24px">
            <Box
              paddingY="12px"
              paddingX="16px"
              backgroundColor="gray50"
              style={{borderRadius: '4px'}}
              data-test-selector="rapid-delivery-countdown"
            >
              <Flex gap="4px" alignItems="center" justifyContent="center" flexWrap="wrap">
                <Text variant="text1">Checkout in the next:</Text>
                <Countdown slotExpiration={expiryTime ?? ''} color="accent0" textVariant="text2" />
                <Text variant="text1">to secure your delivery</Text>
              </Flex>
            </Box>
          </Box>

          {/* Buttons */}
          <Box paddingY="20px" paddingX="16px" backgroundColor="gray50">
            <Flex
              gap="16px"
              direction={['column-reverse', 'column-reverse', 'row']}
              alignItems="stretch"
            >
              <Button
                variant="outlineDark"
                onClick={rest.onClose}
                size="md"
                flex={[null, null, '1']}
                data-test-selector="rapid-change-delivery"
              >
                Change Delivery
              </Button>
              <BaseButton
                onClick={handleContinueShopping}
                variant="primary"
                size="md"
                flex={[null, null, '1']}
                data-test-selector="rapid-continue-shopping"
              >
                <Text
                  variant="unstyled"
                  fontSize="inherit"
                  lineHeight="inherit"
                  fontWeight="inherit"
                >
                  Continue Shopping
                </Text>
              </BaseButton>
            </Flex>
          </Box>
        </Box>
      </Modal>
    )
  }
)
